import React, { useCallback, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserContext } from '../../hooks/useUserContext';
import { Spinner } from '../../components/Spinner';
import { AccountSecurityTab } from './AccountSecurityTab';
import { AlertPreferencesTab } from './AlertPreferencesTab';
import { BusinessProfileTab } from './BusinessProfileTab';
import { SupportAccessSettingsSection } from './SupportAccessSettingsSection';

type TabId = 'profile' | 'alerts' | 'security';

type ToastItem = { id: number; message: string; variant: 'success' | 'error' };

export default function SettingsPage() {
  const { currentUser, userProfile, loading, refreshProfile } = useAuth();
  const { isPersonal } = useUserContext();
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, variant }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  const handleProfileSaved = useCallback(async () => {
    await refreshProfile();
  }, [refreshProfile]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true">
        <Spinner className="h-8 w-8 text-accent" />
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const businessEmail = currentUser.email ?? '';
  const tabs = [
    { id: 'profile' as const, label: isPersonal ? 'Personal Profile' : 'Business Profile' },
    { id: 'alerts' as const, label: 'Alert Preferences' },
    { id: 'security' as const, label: 'Account Security' },
  ];

  return (
    <div className="mx-auto max-w-5xl pb-8">
      <div className="mb-8">
        <SupportAccessSettingsSection userId={currentUser.id} onToast={showToast} />
      </div>

      <div className="mb-6 lg:mb-8">
        <p className="text-sm text-muted lg:hidden">Settings</p>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl lg:hidden">
          Manage your account
        </h2>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
        <nav
          className="flex gap-2 overflow-x-auto pb-1 lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible lg:pb-0"
          aria-label="Settings sections"
        >
          {tabs.map((t) => {
            const selected = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-left text-sm font-medium transition-colors lg:w-full ${
                  selected
                    ? 'bg-accent/15 text-accent ring-1 ring-accent/30'
                    : 'bg-surface/60 text-muted hover:bg-foreground/5 hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          {activeTab === 'profile' ? (
            <BusinessProfileTab
              userProfile={userProfile}
              businessEmail={businessEmail}
              userId={currentUser.id}
              onToast={showToast}
              onProfileSaved={handleProfileSaved}
            />
          ) : null}
          {activeTab === 'alerts' ? (
            <AlertPreferencesTab
              userProfile={userProfile}
              userId={currentUser.id}
              userEmail={businessEmail}
              onToast={showToast}
              onProfileSaved={handleProfileSaved}
            />
          ) : null}
          {activeTab === 'security' ? (
            <AccountSecurityTab
              currentUser={currentUser}
              userProfile={userProfile}
              onToast={showToast}
            />
          ) : null}
        </div>
      </div>

      <div
        className="pointer-events-none fixed bottom-24 left-0 right-0 z-[90] flex flex-col items-center gap-2 px-4 lg:bottom-8"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto max-w-md rounded-lg border px-4 py-3 text-sm font-medium shadow-lg ${
              t.variant === 'success'
                ? 'border-accent/40 bg-navy text-accent'
                : 'border-danger/50 bg-navy text-danger'
            }`}
            role={t.variant === 'error' ? 'alert' : 'status'}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
